import { queryOptions } from '@tanstack/react-query';
import { Domain, } from '@repo/domain';
import { resolveRepository } from '../registry/resolveRepository';
import { applyMinimalFlatPrice } from '../utils/applyMinimalFlatPrice';
const applyMinimalEstimatedPrice = (reservation) => ({
    ...reservation,
    estimatedPrice: reservation.estimatedPrice ? applyMinimalFlatPrice(reservation.estimatedPrice) : undefined,
});
export const reservationKeys = {
    all: ['reservations'],
    get: (filter, scope) => ['reservations', 'list', filter, scope],
    stats: () => ['reservations', 'stats'],
    spanPaymentQuote: (reservationSpanId) => ['reservations', 'spanPaymentQuote', reservationSpanId],
};
export const reservationOptions = {
    get: (filter, scope, options) => queryOptions({
        queryKey: reservationKeys.get(filter, scope),
        queryFn: async () => {
            const repo = await resolveRepository(Domain.Reservation);
            const reservations = await repo.get(filter, scope);
            return reservations.map(applyMinimalEstimatedPrice);
        },
        enabled: options?.enabled,
        refetchInterval: options?.refetchInterval ? (query) => options.refetchInterval(query.state.data) : undefined,
    }),
    stats: () => queryOptions({
        queryKey: reservationKeys.stats(),
        queryFn: async () => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.getStats();
        },
    }),
    spanPaymentQuote: (reservationSpanId) => queryOptions({
        queryKey: reservationKeys.spanPaymentQuote(reservationSpanId),
        queryFn: async () => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.requestReservationSpanPayment({ reservationSpanId: reservationSpanId });
        },
        enabled: reservationSpanId !== undefined,
    }),
    create: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.createReservation(request);
        },
    }),
    swapVehicle: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.swapVehicleInReservation(request);
        },
    }),
    addVehicle: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.addVehicleToReservation(request);
        },
    }),
    removeVehicle: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.removeVehicleFromReservation(request);
        },
    }),
    reassignSpanSpot: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.reassignReservationSpanSpot(request);
        },
    }),
    initiatePayment: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.initiateReservationPayment(request);
        },
    }),
    requestSpanExtension: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.requestReservationSpanExtension(request);
        },
    }),
    initiateSpanExtensionPayment: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.initiateReservationSpanExtensionPayment(request);
        },
    }),
    initiateSpanPayment: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.initiateReservationSpanPayment(request);
        },
    }),
    setRevolving: () => ({
        mutationFn: async (request) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.setReservationRevolving(request);
        },
    }),
    cancel: () => ({
        mutationFn: async (id) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.cancelReservation(id);
        },
    }),
    exportToCsv: () => ({
        mutationFn: async (filter) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.exportToCsv(filter);
        },
    }),
    exportToPdf: () => ({
        mutationFn: async (filter) => {
            const repo = await resolveRepository(Domain.Reservation);
            return repo.exportToPdf(filter);
        },
    }),
};
